import { getJWTToken } from '../../utils/auth';
import type { PeContent, PeReport, ScoreUnit } from '../ProgramEvaluation/types';
import { authHeaders } from '../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type PEGeneration = {
  districtId?: string;
  districtName?: string;
  cognitoSub?: string;
  email?: string;
  schoolYear?: string;
  unitMode?: string;
  generations: number;
  generatedAt?: string;
};

export async function fetchPEGenerations(): Promise<PEGeneration[]> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/admin/program-evaluations`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`admin-program-evaluations fetch failed: ${response.status}`);
  }
  const data = (await response.json()) as { generations?: PEGeneration[] | null };
  return data.generations ?? [];
}

export type PEReportVersion = {
  version: number;
  unitMode?: string;
  generatedAt?: string;
};

export type PEReportArtifact = {
  districtId: string;
  schoolYear: string;
  version: number;
  generatedAt?: string;
  report: PeReport | null;
  content: PeContent | null;
  versions: PEReportVersion[];
};

type PEReportResponse = Omit<PEReportArtifact, 'content' | 'versions'> & {
  unitMode?: ScoreUnit;
  content: (Omit<PeContent, 'unit_mode'> & { unit_mode?: ScoreUnit }) | null;
  versions?: PEReportVersion[] | null;
};

export async function fetchPEReport(
  cognitoSub: string,
  schoolYear: string,
  version?: number,
): Promise<PEReportArtifact | null> {
  const token = await getJWTToken();
  const path = `/admin/program-evaluations/${encodeURIComponent(cognitoSub)}/${encodeURIComponent(schoolYear)}`;
  const query = version === undefined ? '' : `?version=${version}`;
  const response = await fetch(`${apiUrl}${path}${query}`, {
    headers: authHeaders(token),
  });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`admin-program-evaluation fetch failed: ${response.status}`);
  }
  const data = (await response.json()) as PEReportResponse;
  return {
    districtId: data.districtId,
    schoolYear: data.schoolYear,
    version: data.version,
    generatedAt: data.generatedAt,
    report: data.report,
    content: data.content
      ? { ...data.content, unit_mode: data.content.unit_mode ?? data.unitMode ?? 'proficiency_level' }
      : null,
    versions: data.versions ?? [],
  };
}
